import React, { useState, useMemo } from 'react';
import { Btn } from './ui/Card';

// ── Templates de la Bibliothèque ───────────────────────────
export const TEMPLATES = {
  camp_jeunes: {
    label: '⛺ Camp Jeunesse',
    description: "Camp de formation spirituelle et d'activités pour les jeunes",
    objectives: '• Renforcer la foi et la connaissance biblique des participants\n• Créer des liens fraternels durables entre jeunes\n• Former des jeunes leaders engagés\n• Organiser des activités sportives et artistiques',
    team_members: 'Pasteur responsable, Coordinateur camp, 6 moniteurs bénévoles',
    phases: [
      { name: 'Préparation', icon: '📋', tasks: ['Recrutement des moniteurs', 'Achat du matériel', 'Inscriptions des participants', 'Communication et publicité', 'Réservation du site'] },
      { name: 'Réalisation', icon: '⛺', tasks: ['Animation quotidienne', 'Logistique et hébergement', 'Suivi spirituel', 'Activités sportives', 'Soirées de louange'] },
      { name: 'Évaluation',  icon: '📊', tasks: ['Collecte retours participants', 'Rapport financier', "Rapport d'activités", 'Recommandations futures'] },
    ],
    risks: [
      { description: 'Météo défavorable', severity: 'medium', mitigation: 'Plan de repli en salle prévu' },
      { description: 'Désistements de participants', severity: 'low', mitigation: "Liste d'attente maintenue" },
    ],
  },
  conference: {
    label: '🎤 Conférence Évangélique',
    description: "Conférence d'évangélisation et d'enseignement biblique",
    objectives: "• Évangéliser et toucher de nouvelles personnes\n• Approfondir l'enseignement de la Parole\n• Renforcer la communion fraternelle\n• Documenter et diffuser les enseignements",
    team_members: 'Conférencier invité, Coordinateur logistique, Équipe louange, Équipe accueil',
    phases: [
      { name: 'Préparation', icon: '📋', tasks: ['Invitation du conférencier', 'Réservation de la salle', 'Communication et affiches', 'Équipe de louange', 'Logistique technique'] },
      { name: 'Réalisation', icon: '🎤', tasks: ['Accueil des participants', 'Sessions plénières', 'Ateliers thématiques', 'Restauration', 'Enregistrement vidéo'] },
      { name: 'Évaluation',  icon: '📊', tasks: ['Rapport de participation', 'Retours des participants', 'Rapport financier', 'Archives et publications'] },
    ],
    risks: [
      { description: 'Faible participation', severity: 'medium', mitigation: 'Renforcer la communication 2 semaines avant' },
      { description: 'Problème technique sonorisation', severity: 'high', mitigation: 'Équipement de secours prévu' },
    ],
  },
  distribution: {
    label: '🤝 Distribution Aide Humanitaire',
    description: "Distribution d'aide alimentaire et matérielle aux bénéficiaires",
    objectives: "• Toucher 500 familles dans le besoin\n• Assurer une distribution équitable et transparente\n• Sensibiliser aux bonnes pratiques d'hygiène\n• Documenter tous les bénéficiaires",
    team_members: 'Coordinateur distribution, Équipe logistique, 10 volontaires terrain, Responsable enregistrement',
    phases: [
      { name: 'Préparation', icon: '📋', tasks: ['Identification des bénéficiaires', 'Collecte et achat des denrées', 'Conditionnement des colis', 'Formation des volontaires', 'Plan de distribution'] },
      { name: 'Réalisation', icon: '🚚', tasks: ['Transport vers les zones', 'Enregistrement des bénéficiaires', 'Distribution des colis', 'Documentation photographique', 'Gestion de la sécurité'] },
      { name: 'Évaluation',  icon: '📊', tasks: ['Décompte final bénéficiaires', 'Rapport financier', "Rapport d'impact", 'Recommandations pour la suite'] },
    ],
    risks: [
      { description: 'Accès difficile aux zones éloignées', severity: 'high', mitigation: 'Transport adapté (4x4) planifié' },
      { description: 'Conflits lors de la distribution', severity: 'medium', mitigation: 'Protocole de sécurité établi' },
    ],
  },
  formation: {
    label: '📚 Formation Leadership',
    description: 'Programme de formation pour les leaders et responsables de ministères',
    objectives: '• Former 30 leaders en gestion et communication\n• Développer les compétences managériales chrétiennes\n• Renforcer les valeurs du leadership serviteur\n• Certifier les participants à la fin du programme',
    team_members: 'Formateur principal, 3 facilitateurs, Coordinateur pédagogique, Responsable administratif',
    phases: [
      { name: 'Préparation', icon: '📋', tasks: ['Élaboration du curriculum', 'Recrutement des formateurs', 'Inscriptions des participants', 'Préparation des supports', 'Logistique salle et matériel'] },
      { name: 'Réalisation', icon: '📚', tasks: ['Modules théoriques', 'Ateliers pratiques', 'Études de cas', 'Mises en situation', 'Évaluations intermédiaires'] },
      { name: 'Évaluation',  icon: '🏆', tasks: ['Examens finaux', 'Certification des participants', 'Rapport pédagogique', 'Rapport financier', 'Suivi post-formation'] },
    ],
    risks: [
      { description: 'Abandons en cours de formation', severity: 'medium', mitigation: 'Sélection rigoureuse et engagement préalable signé' },
      { description: 'Formateur indisponible', severity: 'high', mitigation: 'Formateur de remplacement identifié' },
    ],
  },
};

// ── Helpers ────────────────────────────────────────────────
const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
const parseLocalDate = s => { if (!s) return null; const [y,m,d]=String(s).slice(0,10).split('-').map(Number); return new Date(y,m-1,d); };
const parseJSON = (v, def) => { try { return Array.isArray(v) ? v : (JSON.parse(v) || def); } catch { return def; } };
const phaseProgress = p => { if (!p.tasks?.length) return 0; return Math.round(p.tasks.filter(t => t.done).length / p.tasks.length * 100); };

const PHASE_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
const SEVERITY = {
  low:    { bg: '#dcfce7', color: '#166534', border: '#10b981', label: 'Faible' },
  medium: { bg: '#fef9c3', color: '#854d0e', border: '#f59e0b', label: 'Moyen' },
  high:   { bg: '#fee2e2', color: '#991b1b', border: '#ef4444', label: 'Élevé' },
};

// ── Gantt Chart ────────────────────────────────────────────
function GanttChart({ project, phases }) {
  const start = parseLocalDate(project.start_date);
  const end   = parseLocalDate(project.end_date);

  if (!start || !end || end <= start) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--gray-400)', fontStyle: 'italic' }}>
        Définissez les dates de début et de fin du projet pour afficher le diagramme de Gantt.
      </div>
    );
  }

  const totalMs = end - start;

  const toPercent = (dateStr) => {
    if (!dateStr) return null;
    const d = parseLocalDate(dateStr);
    return Math.max(0, Math.min(100, (d - start) / totalMs * 100));
  };

  // Month tick marks
  const months = [];
  const cur = new Date(start);
  cur.setDate(1);
  while (cur <= end) {
    months.push({ date: new Date(cur), pct: toPercent(cur.toISOString().split('T')[0]) });
    cur.setMonth(cur.getMonth() + 1);
  }

  const todayPct = toPercent(new Date().toISOString().split('T')[0]);

  return (
    <div>
      <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: 16 }}>
        📅 {start.toLocaleDateString('fr-FR')} → {end.toLocaleDateString('fr-FR')}
        <span style={{ marginLeft: 10, color: 'var(--gray-400)' }}>
          ({Math.round(totalMs / 86400000)} jours)
        </span>
      </p>

      {phases.length === 0 && (
        <p style={{ textAlign: 'center', color: 'var(--gray-400)', fontStyle: 'italic', padding: 32 }}>
          Définissez des phases dans l'onglet "Phases & Tâches" pour les voir ici.
        </p>
      )}

      <div style={{ position: 'relative' }}>
        {/* Month header row */}
        <div style={{ display: 'flex', marginBottom: 8 }}>
          <div style={{ width: 150, flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative', height: 22, background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
            {months.map((m, i) => (
              <div key={i} style={{ position: 'absolute', left: `${m.pct}%`, top: 0, bottom: 0, borderLeft: '1px dashed var(--gray-300)', paddingLeft: 3 }}>
                <span style={{ fontSize: '0.65rem', color: 'var(--gray-500)', lineHeight: '22px' }}>
                  {m.date.toLocaleDateString('fr-FR', { month: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Phase rows */}
        {phases.map((phase, idx) => {
          const left  = toPercent(phase.start_date);
          const right = toPercent(phase.end_date);
          const width = left !== null && right !== null ? right - left : null;
          const prog  = phaseProgress(phase);
          const color = PHASE_COLORS[idx % PHASE_COLORS.length];

          return (
            <div key={phase.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ width: 150, flexShrink: 0, paddingRight: 12, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                <span>{phase.icon}</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{phase.name}</span>
              </div>
              <div style={{ flex: 1, position: 'relative', height: 32, background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
                {months.map((m, i) => (
                  <div key={i} style={{ position: 'absolute', left: `${m.pct}%`, top: 0, bottom: 0, borderLeft: '1px dashed var(--gray-200)', pointerEvents: 'none' }} />
                ))}
                {todayPct !== null && todayPct >= 0 && todayPct <= 100 && (
                  <div style={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, borderLeft: '2px solid var(--red)', zIndex: 5, pointerEvents: 'none' }} />
                )}
                {width !== null ? (
                  <div title={`${phase.name} — ${prog}% complété`} style={{
                    position: 'absolute', left: `${left}%`, width: `${Math.max(width, 1.5)}%`,
                    top: 4, bottom: 4, borderRadius: 4, background: color, opacity: 0.85,
                    display: 'flex', alignItems: 'center', overflow: 'hidden',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                  }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${prog}%`, background: 'rgba(0,0,0,0.18)', borderRadius: 4 }} />
                    <span style={{ position: 'relative', paddingLeft: 6, fontSize: '0.68rem', color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>{prog}%</span>
                  </div>
                ) : (
                  <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>
                    Dates non définies
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Today marker label */}
        {todayPct !== null && todayPct >= 0 && todayPct <= 100 && (
          <div style={{ paddingLeft: 150 }}>
            <div style={{ position: 'relative', height: 0 }}>
              <span style={{ position: 'absolute', left: `${todayPct}%`, top: -8, transform: 'translateX(-50%)', background: 'var(--red)', color: 'white', fontSize: '0.62rem', padding: '2px 5px', borderRadius: 3, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Aujourd'hui
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ProjectDetail Component ───────────────────────────
export default function ProjectDetail({ project, canEdit, onUpdate, onClose }) {
  const [tab, setTab] = useState('overview');
  const [phases,      setPhases]      = useState(() => parseJSON(project.phases, []));
  const [objectives,  setObjectives]  = useState(project.objectives || '');
  const [teamMembers, setTeamMembers] = useState(project.team_members || '');
  const [risks,       setRisks]       = useState(() => parseJSON(project.risks, []));
  const [editObj,     setEditObj]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [newTaskText, setNewTaskText] = useState({});
  const [newRisk,     setNewRisk]     = useState({ description: '', severity: 'medium', mitigation: '' });
  const [showRiskForm, setShowRiskForm] = useState(false);

  const totalTasks = useMemo(() => phases.reduce((s, p) => s + (p.tasks?.length || 0), 0), [phases]);
  const doneTasks  = useMemo(() => phases.reduce((s, p) => s + (p.tasks?.filter(t => t.done).length || 0), 0), [phases]);
  const overallPct = totalTasks ? Math.round(doneTasks / totalTasks * 100) : (project.progress || 0);
  const budgetTotal = parseFloat(project.budget_total || project.budget || 0);
  const budgetUsed  = parseFloat(project.budget_used || 0);
  const budgetPct   = budgetTotal > 0 ? Math.round(budgetUsed / budgetTotal * 100) : 0;

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(project.id, {
        phases: JSON.stringify(phases),
        objectives,
        team_members: teamMembers,
        risks: JSON.stringify(risks),
        progress: overallPct,
      });
    } finally { setSaving(false); }
  };

  // ── Phase operations ──
  const addPhase = () => setPhases(prev => [...prev, { id: newId(), name: 'Nouvelle phase', icon: '📋', start_date: '', end_date: '', budget: 0, budget_used: 0, tasks: [] }]);
  const removePhase = id => setPhases(prev => prev.filter(p => p.id !== id));
  const updatePhase = (id, key, val) => setPhases(prev => prev.map(p => p.id === id ? { ...p, [key]: val } : p));
  const addTask = (phaseId) => {
    const name = (newTaskText[phaseId] || '').trim();
    if (!name) return;
    setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, tasks: [...(p.tasks || []), { id: newId(), name, done: false }] } : p));
    setNewTaskText(prev => ({ ...prev, [phaseId]: '' }));
  };
  const toggleTask = (phaseId, taskId) => setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, tasks: p.tasks.map(t => t.id === taskId ? { ...t, done: !t.done } : t) } : p));
  const removeTask = (phaseId, taskId) => setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, tasks: p.tasks.filter(t => t.id !== taskId) } : p));

  // ── Risk operations ──
  const addRisk = () => {
    if (!newRisk.description.trim()) return;
    setRisks(prev => [...prev, { ...newRisk, id: newId() }]);
    setNewRisk({ description: '', severity: 'medium', mitigation: '' });
    setShowRiskForm(false);
  };
  const removeRisk = id => setRisks(prev => prev.filter(r => r.id !== id));

  const TABS = [
    { key: 'overview', label: "🏠 Vue d'ensemble" },
    { key: 'phases',   label: `📋 Phases & Tâches${totalTasks ? ` (${doneTasks}/${totalTasks})` : ''}` },
    { key: 'gantt',    label: '📅 Gantt' },
    { key: 'budget',   label: '💰 Budget' },
    { key: 'risks',    label: `⚠️ Risques${risks.length ? ` (${risks.length})` : ''}` },
  ];

  return (
    <div>
      {/* Project info bar */}
      <div style={{ padding: '14px 24px', background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%)', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1rem', marginBottom: 4 }}>{project.name}</h3>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 9px', borderRadius: 20, fontSize: '0.68rem' }}>
                📅 {project.start_date ? parseLocalDate(project.start_date).toLocaleDateString('fr-FR') : '—'} → {project.end_date ? parseLocalDate(project.end_date).toLocaleDateString('fr-FR') : '—'}
              </span>
              {(project.coordinator || project.responsable) && (
                <span style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 9px', borderRadius: 20, fontSize: '0.68rem' }}>
                  👤 {project.coordinator || project.responsable}
                </span>
              )}
            </div>
          </div>
          {/* Progress pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>{overallPct}%</div>
              <div style={{ fontSize: '0.62rem', opacity: 0.7, marginTop: 2 }}>Avancement</div>
            </div>
            <div style={{ width: 100, height: 7, background: 'rgba(255,255,255,0.25)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ width: `${overallPct}%`, height: '100%', background: '#c9a84c', borderRadius: 99, transition: 'width 0.3s' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)', background: 'white', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '11px 15px', fontSize: '0.78rem', fontWeight: tab === t.key ? 700 : 500,
            color: tab === t.key ? 'var(--navy)' : 'var(--gray-500)',
            borderBottom: tab === t.key ? '2px solid var(--navy)' : '2px solid transparent',
            background: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: '20px 24px', maxHeight: '50vh', overflowY: 'auto' }}>

        {/* ── Vue d'ensemble ── */}
        {tab === 'overview' && (
          <div>
            {/* KPI row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 20 }}>
              {[
                { icon: '✅', label: 'Tâches', value: `${doneTasks} / ${totalTasks}`, color: 'var(--emerald)' },
                { icon: '📋', label: 'Phases', value: phases.length, color: '#6366f1' },
                { icon: '💰', label: 'Budget utilisé', value: `${budgetPct}%`, color: budgetPct > 90 ? 'var(--red)' : 'var(--gold)' },
                { icon: '⚠️', label: 'Risques', value: risks.length, color: risks.some(r => r.severity === 'high') ? 'var(--red)' : 'var(--orange)' },
              ].map(k => (
                <div key={k.label} style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 9, padding: '12px 14px', borderLeft: `3px solid ${k.color}` }}>
                  <div style={{ fontSize: '1.1rem', marginBottom: 3 }}>{k.icon}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: 3 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Objectives */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Objectifs SMART</label>
                {canEdit && <Btn size="sm" variant="ghost" onClick={() => setEditObj(v => !v)} style={{ fontSize: '0.78rem', padding: '3px 8px' }}>{editObj ? '✓ OK' : '✏️ Modifier'}</Btn>}
              </div>
              {editObj ? (
                <textarea value={objectives} onChange={e => setObjectives(e.target.value)} rows={5}
                  placeholder={"• Objectif 1 (Spécifique, Mesurable, Atteignable...)\n• Objectif 2\n• Objectif 3"}
                  style={{ resize: 'vertical', lineHeight: 1.7 }} />
              ) : (
                <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 9, padding: '12px 14px', minHeight: 56 }}>
                  {objectives
                    ? objectives.split('\n').map((line, i) => <p key={i} style={{ fontSize: '0.84rem', lineHeight: 1.7, color: 'var(--navy)' }}>{line}</p>)
                    : <span style={{ color: 'var(--gray-400)', fontStyle: 'italic', fontSize: '0.82rem' }}>Aucun objectif défini. Cliquez sur Modifier pour ajouter.</span>
                  }
                </div>
              )}
            </div>

            {/* Team members */}
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Équipe Projet</label>
              {canEdit
                ? <input value={teamMembers} onChange={e => setTeamMembers(e.target.value)} placeholder="Ex: Pasteur X (responsable), Coordinateur Y, 3 volontaires..." />
                : <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 9, padding: '10px 14px', fontSize: '0.84rem', color: teamMembers ? 'var(--navy)' : 'var(--gray-400)', fontStyle: teamMembers ? 'normal' : 'italic' }}>
                    {teamMembers || 'Équipe non définie'}
                  </div>
              }
            </div>
          </div>
        )}

        {/* ── Phases & Tâches ── */}
        {tab === 'phases' && (
          <div>
            {phases.length === 0 && (
              <p style={{ textAlign: 'center', color: 'var(--gray-400)', fontStyle: 'italic', padding: '24px 0 16px' }}>
                Aucune phase. Ajoutez des phases manuellement ou appliquez un modèle depuis la liste des projets.
              </p>
            )}
            {phases.map((phase, idx) => {
              const prog  = phaseProgress(phase);
              const color = PHASE_COLORS[idx % PHASE_COLORS.length];
              return (
                <div key={phase.id} style={{ border: `1.5px solid ${color}30`, borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>
                  {/* Phase header */}
                  <div style={{ background: `${color}0d`, borderBottom: `1px solid ${color}25`, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                    {canEdit
                      ? <input value={phase.icon} onChange={e => updatePhase(phase.id, 'icon', e.target.value)} style={{ width: 32, textAlign: 'center', border: 'none', background: 'transparent', fontSize: '1rem', padding: 0 }} />
                      : <span style={{ fontSize: '1rem' }}>{phase.icon}</span>
                    }
                    {canEdit
                      ? <input value={phase.name} onChange={e => updatePhase(phase.id, 'name', e.target.value)} style={{ fontWeight: 700, fontSize: '0.88rem', border: 'none', background: 'transparent', color: 'var(--navy)', flex: 1, minWidth: 100, padding: 0, outline: 'none' }} />
                      : <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--navy)', flex: 1 }}>{phase.name}</span>
                    }
                    <span style={{ fontSize: '0.73rem', fontWeight: 600, color }}>{prog}%</span>
                    <div style={{ width: 56, height: 5, background: 'var(--gray-200)', borderRadius: 99, overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ width: `${prog}%`, height: '100%', background: color, borderRadius: 99 }} />
                    </div>
                    {canEdit && <button onClick={() => removePhase(phase.id)} style={{ background: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: '0.85rem', padding: '1px 4px', borderRadius: 4, flexShrink: 0 }}>✕</button>}
                  </div>

                  {/* Phase dates & budget */}
                  {canEdit && (
                    <div style={{ display: 'flex', gap: 8, padding: '8px 14px', background: 'white', borderBottom: `1px solid ${color}15`, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.73rem', color: 'var(--gray-500)' }}>📅</span>
                      <input type="date" value={phase.start_date || ''} onChange={e => updatePhase(phase.id, 'start_date', e.target.value)} style={{ fontSize: '0.73rem', padding: '3px 7px', width: 128 }} />
                      <span style={{ fontSize: '0.73rem', color: 'var(--gray-400)' }}>→</span>
                      <input type="date" value={phase.end_date || ''} onChange={e => updatePhase(phase.id, 'end_date', e.target.value)} style={{ fontSize: '0.73rem', padding: '3px 7px', width: 128 }} />
                      <span style={{ fontSize: '0.73rem', color: 'var(--gray-500)', marginLeft: 8 }}>💰</span>
                      <input type="number" value={phase.budget || ''} onChange={e => updatePhase(phase.id, 'budget', e.target.value)} placeholder="Budget Ar" style={{ fontSize: '0.73rem', padding: '3px 7px', width: 110 }} />
                    </div>
                  )}

                  {/* Tasks */}
                  <div style={{ padding: '8px 14px 12px' }}>
                    {(phase.tasks || []).map(task => (
                      <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--gray-100)' }}>
                        <input type="checkbox" checked={!!task.done} onChange={() => toggleTask(phase.id, task.id)}
                          style={{ width: 15, height: 15, accentColor: color, cursor: 'pointer', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '0.83rem', color: task.done ? 'var(--gray-400)' : 'var(--navy)', textDecoration: task.done ? 'line-through' : 'none' }}>
                          {task.name}
                        </span>
                        {canEdit && <button onClick={() => removeTask(phase.id, task.id)} style={{ background: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: '0.78rem', padding: '1px 3px' }}>✕</button>}
                      </div>
                    ))}
                    {phase.tasks?.length === 0 && <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontStyle: 'italic', margin: '8px 0 4px' }}>Aucune tâche pour cette phase</p>}
                    {canEdit && (
                      <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                        <input value={newTaskText[phase.id] || ''} onChange={e => setNewTaskText(p => ({ ...p, [phase.id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && addTask(phase.id)}
                          placeholder="Nouvelle tâche (Entrée pour valider)..."
                          style={{ flex: 1, fontSize: '0.8rem', padding: '6px 10px' }} />
                        <Btn size="sm" variant="primary" onClick={() => addTask(phase.id)}>+ Ajouter</Btn>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {canEdit && (
              <Btn variant="secondary" onClick={addPhase} style={{ width: '100%', justifyContent: 'center' }}>
                + Ajouter une phase
              </Btn>
            )}
          </div>
        )}

        {/* ── Gantt ── */}
        {tab === 'gantt' && <GanttChart project={project} phases={phases} />}

        {/* ── Budget ── */}
        {tab === 'budget' && (
          <div>
            {/* Global budget bar */}
            <div style={{ padding: '16px 18px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 10, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)' }}>Budget global</span>
                <span style={{ fontSize: '0.84rem', color: 'var(--gray-600)' }}>
                  <strong style={{ color: budgetPct > 90 ? 'var(--red)' : 'var(--navy)' }}>{budgetUsed.toLocaleString('fr-FR')}</strong>
                  {' / '}{budgetTotal.toLocaleString('fr-FR')} Ar
                  <span style={{ marginLeft: 8, fontWeight: 700, color: budgetPct > 90 ? 'var(--red)' : 'var(--emerald)' }}>({budgetPct}%)</span>
                </span>
              </div>
              <div style={{ height: 10, background: 'var(--gray-200)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(budgetPct, 100)}%`, background: budgetPct > 90 ? 'var(--red)' : budgetPct > 70 ? 'var(--orange)' : 'var(--emerald)', borderRadius: 99, transition: 'width 0.4s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                <span>Dépensé</span>
                <span>Restant : <strong>{Math.max(0, budgetTotal - budgetUsed).toLocaleString('fr-FR')} Ar</strong></span>
              </div>
            </div>

            {/* Per-phase breakdown */}
            {phases.some(p => parseFloat(p.budget) > 0) ? (
              <div>
                <h4 style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Répartition par phase</h4>
                {phases.filter(p => parseFloat(p.budget) > 0).map((phase, idx) => {
                  const pBudget = parseFloat(phase.budget || 0);
                  const pUsed   = parseFloat(phase.budget_used || 0);
                  const pPct    = pBudget > 0 ? Math.round(pUsed / pBudget * 100) : 0;
                  const color   = PHASE_COLORS[idx % PHASE_COLORS.length];
                  const sharePct = budgetTotal > 0 ? Math.round(pBudget / budgetTotal * 100) : 0;
                  return (
                    <div key={phase.id} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: '0.83rem', fontWeight: 600, color: 'var(--navy)' }}>{phase.icon} {phase.name}</span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                          {pBudget.toLocaleString('fr-FR')} Ar
                          <span style={{ color: 'var(--gray-400)', marginLeft: 4 }}>({sharePct}% du total)</span>
                        </span>
                      </div>
                      <div style={{ height: 7, background: 'var(--gray-200)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pPct}%`, background: color, borderRadius: 99 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: 'var(--gray-400)', fontStyle: 'italic', fontSize: '0.84rem', textAlign: 'center', padding: 24 }}>
                Définissez un budget par phase dans l'onglet "Phases & Tâches" pour voir la répartition.
              </p>
            )}
          </div>
        )}

        {/* ── Risques ── */}
        {tab === 'risks' && (
          <div>
            {risks.length === 0 && !showRiskForm && (
              <p style={{ textAlign: 'center', color: 'var(--gray-400)', fontStyle: 'italic', padding: '24px 0 16px' }}>
                Aucun risque identifié pour ce projet.
              </p>
            )}
            {risks.map(risk => {
              const sev = SEVERITY[risk.severity] || SEVERITY.medium;
              return (
                <div key={risk.id} style={{ border: '1px solid var(--gray-200)', borderLeft: `4px solid ${sev.border}`, borderRadius: 9, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.84rem', color: 'var(--navy)' }}>{risk.description}</span>
                        <span style={{ background: sev.bg, color: sev.color, fontSize: '0.68rem', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>{sev.label}</span>
                      </div>
                      {risk.mitigation && <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)', margin: 0 }}>↳ Mitigation : {risk.mitigation}</p>}
                    </div>
                    {canEdit && <button onClick={() => removeRisk(risk.id)} style={{ background: 'none', color: 'var(--gray-300)', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 4px', flexShrink: 0 }}>✕</button>}
                  </div>
                </div>
              );
            })}
            {canEdit && !showRiskForm && (
              <Btn variant="secondary" onClick={() => setShowRiskForm(true)} style={{ width: '100%', justifyContent: 'center' }}>
                + Identifier un risque
              </Btn>
            )}
            {showRiskForm && (
              <div style={{ border: '1.5px solid var(--gray-200)', borderRadius: 10, padding: '14px 16px', background: 'var(--gray-50)', marginTop: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px', gap: 10, marginBottom: 10 }}>
                  <input value={newRisk.description} onChange={e => setNewRisk(p => ({ ...p, description: e.target.value }))} placeholder="Description du risque..." />
                  <select value={newRisk.severity} onChange={e => setNewRisk(p => ({ ...p, severity: e.target.value }))}>
                    <option value="low">Faible</option>
                    <option value="medium">Moyen</option>
                    <option value="high">Élevé</option>
                  </select>
                </div>
                <input value={newRisk.mitigation} onChange={e => setNewRisk(p => ({ ...p, mitigation: e.target.value }))} placeholder="Mesure de mitigation..." style={{ marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn size="sm" variant="secondary" onClick={() => setShowRiskForm(false)}>Annuler</Btn>
                  <Btn size="sm" variant="primary" onClick={addRisk}>Ajouter</Btn>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '12px 24px', borderTop: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
        <Btn variant="secondary" onClick={onClose}>Fermer</Btn>
        {canEdit && (
          <Btn variant="gold" onClick={save} disabled={saving}>
            {saving ? 'Enregistrement...' : '💾 Enregistrer les modifications'}
          </Btn>
        )}
      </div>
    </div>
  );
}
